import React, { useMemo } from 'react';
import {
  pickEffectiveDetail,
  useAmhsDetailLevel,
} from '../../rendering';
import {
  AWP_300_DEFAULTS,
  clampAwp300PortCount,
  normalizeAwp300Status,
  type Awp300Props,
} from './awp300Types';
import { resolveAwp300Layout } from './awp300Layout';
import { Awp300Body } from './Awp300Body';
import { Awp300Glass } from './Awp300Glass';
import { Awp300LoadPorts } from './Awp300LoadPorts';
import { Awp300Chamber } from './Awp300Chamber';
import { Awp300Robot } from './Awp300Robot';
import { Awp300Racks } from './Awp300Racks';
import { Awp300ControlPanel } from './Awp300ControlPanel';
import { EquipmentLights } from '../FabProcessTool/EquipmentLights';
import { StatusLights } from '../FabProcessTool/StatusLights';

/**
 * AWP-300 Advanced Wafer Processing Module — reference-matched procedural 3D kit.
 * White/black frame, cyan-blue glass enclosure, dual load ports, central process chamber.
 */
export const Awp300: React.FC<Awp300Props> = ({
  id = 'AWP-300',
  width = AWP_300_DEFAULTS.width,
  depth = AWP_300_DEFAULTS.depth,
  height = AWP_300_DEFAULTS.height,
  ports = 2,
  status = 'IDLE',
  occupiedPorts = [],
  portFractions,
  detail: detailProp,
  autoDetail = false,
  opacity = 1,
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  onSelect,
}) => {
  const portCount = clampAwp300PortCount(ports);
  const eqStatus = normalizeAwp300Status(status);
  const autoTier = useAmhsDetailLevel(position, autoDetail);
  const detail = pickEffectiveDetail(detailProp, autoTier, autoDetail);
  const layout = useMemo(() => resolveAwp300Layout(width, height, depth), [width, height, depth]);

  const occupied = useMemo(
    () => occupiedPorts.filter((i) => i >= 0 && i < portCount),
    [occupiedPorts, portCount]
  );

  const showDetail = detail === 'high';
  const showGlass = detail !== 'very-low';

  return (
    <group
      name={`Awp300-${id}`}
      position={position}
      rotation={rotation}
      onClick={onSelect ? (e) => { e.stopPropagation(); onSelect(); } : undefined}
    >
      <Awp300Body layout={layout} opacity={opacity} ledIntensity={1.1} />
      <Awp300Racks layout={layout} opacity={opacity} />
      {showGlass ? <Awp300Glass layout={layout} detail={detail} opacity={opacity} /> : null}
      <Awp300Chamber layout={layout} opacity={opacity} ledIntensity={1.15} />
      <Awp300Robot layout={layout} opacity={opacity} detail={detail} ledIntensity={1.1} />
      <Awp300LoadPorts
        layout={layout}
        portCount={portCount}
        occupiedPorts={occupied}
        portFractions={portFractions}
        opacity={opacity}
        detail={detail}
      />
      {showDetail ? (
        <>
          <Awp300ControlPanel layout={layout} id={id} opacity={opacity} />
          <EquipmentLights
            width={width}
            height={height}
            depth={depth}
            opacity={opacity}
            ledIntensity={1.2}
          />
          <StatusLights
            width={width}
            height={height}
            depth={depth}
            status={eqStatus}
            opacity={opacity}
          />
        </>
      ) : null}
    </group>
  );
};

export default Awp300;
